import React, { useRef, useState } from "react";
import { RotateCcw, Move } from "lucide-react";
import { useApp } from "@/context/AppContext";

export const DEFAULT_POS = { x: 50, y: 50 };
export const posStyle = p => ({ objectPosition: `${p?.x ?? 50}% ${p?.y ?? 50}%` });

export default function CardImagePositioner({ src, value, onChange }) {
  const { t } = useApp();
  const frame = useRef(null);
  const nat = useRef(null);
  const drag = useRef(null);
  const [dragging, setDragging] = useState(false);
  const pos = value || DEFAULT_POS;
  const clamp = v => Math.min(100, Math.max(0, v));

  const overflow = () => {
    const el = frame.current; const n = nat.current;
    if (!el || !n) return { w: 0, h: 0 };
    const fw = el.clientWidth, fh = el.clientHeight;
    const s = Math.max(fw / n.w, fh / n.h);
    return { w: n.w * s - fw, h: n.h * s - fh };
  };
  const down = e => { drag.current = { x: e.clientX, y: e.clientY, px: pos.x, py: pos.y }; setDragging(true); e.currentTarget.setPointerCapture(e.pointerId); };
  const move = e => {
    if (!drag.current) return;
    const o = overflow();
    const nx = o.w > 1 ? clamp(drag.current.px - ((e.clientX - drag.current.x) / o.w) * 100) : 50;
    const ny = o.h > 1 ? clamp(drag.current.py - ((e.clientY - drag.current.y) / o.h) * 100) : 50;
    onChange({ x: Math.round(nx * 10) / 10, y: Math.round(ny * 10) / 10 });
  };
  const up = () => { drag.current = null; setDragging(false); };
  const isDefault = pos.x === 50 && pos.y === 50;

  return (
    <div data-testid="card-positioner" className="w-[220px]">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="eyebrow">{t("cardPreview")}</span>
        <button type="button" onClick={() => onChange({ ...DEFAULT_POS })} disabled={isDefault} className="eyebrow !text-[10px] text-primary hover:underline disabled:opacity-40 disabled:no-underline inline-flex items-center gap-1" data-testid="card-position-reset"><RotateCcw className="w-3 h-3" />{t("resetPosition")}</button>
      </div>
      <div ref={frame} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
        className={`relative w-[220px] aspect-[4/5] overflow-hidden border border-foreground bg-muted select-none ${dragging ? "cursor-grabbing" : "cursor-grab"}`}
        style={{ touchAction: "none" }} data-testid="card-preview-frame">
        <img src={src} alt="" draggable={false} onLoad={e => { nat.current = { w: e.target.naturalWidth, h: e.target.naturalHeight }; }}
          className="gg-flyer absolute inset-0 pointer-events-none" style={posStyle(pos)} data-testid="card-preview-img" />
        {!dragging && <span className="absolute bottom-1.5 right-1.5 eyebrow !text-[9px] bg-background/90 px-1.5 py-0.5 inline-flex items-center gap-1 pointer-events-none"><Move className="w-3 h-3" />{t("dragToPosition")}</span>}
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground" data-testid="card-position-value">{t("cardPreviewHint")}</p>
    </div>
  );
}
